/**
 * Catalog rules shared by the staff app and the tests. The server re-validates everything; these
 * helpers exist so forms can validate before sending a command and so money is handled in one place.
 *
 * Money is always carried as an integer number of minor units (centavos for PHP). Nothing in the
 * domain ever holds a floating-point amount.
 */

export const PRODUCT_SHAPES = ['square', 'circle', 'rounded', 'octagon'] as const;
export type ProductShape = (typeof PRODUCT_SHAPES)[number];

/** V1 sells by unit only; "weight" stays a Phase 0 idea until a scale exists. */
export const SOLD_BY = ['unit'] as const;
export type SoldBy = (typeof SOLD_BY)[number];

export interface ImageMeta {
  mime: string;
  size: number;
  width?: number;
  height?: number;
}

/** Upper bound for any single amount, in minor units (1 000 000 000.00 in a 2-decimal currency). */
export const MAX_MINOR = 100_000_000_000;

/**
 * Parse an amount typed by a person ("120", "120.5", "1,250.00", " 95 ") into minor units.
 * Returns null when the text is not an amount, has too many decimals, or is out of range.
 */
export function parseMoneyToMinor(
  input: string,
  options: { fractionDigits?: number; allowNegative?: boolean } = {},
): number | null {
  const fd = options.fractionDigits ?? 2;
  const text = input.replace(/[,\s]/g, '');
  const m = /^(-)?(\d+)(?:\.(\d*))?$/.exec(text);
  if (!m) return null;
  const negative = m[1] === '-';
  if (negative && !options.allowNegative) return null;
  const whole = m[2] ?? '0';
  const frac = m[3] ?? '';
  if (frac.length > fd) return null;
  const minor = Number(whole) * 10 ** fd + Number((frac + '0'.repeat(fd)).slice(0, fd));
  if (!Number.isSafeInteger(minor) || minor > MAX_MINOR) return null;
  return negative ? -minor : minor;
}

/** Minor units back to the text an input field shows ("12050" -> "120.50"). */
export function minorToInput(minor: number, fractionDigits = 2): string {
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  const base = 10 ** fractionDigits;
  const whole = Math.floor(abs / base);
  const frac = String(abs % base).padStart(fractionDigits, '0');
  return fractionDigits === 0 ? `${sign}${whole}` : `${sign}${whole}.${frac}`;
}

/** Display formatting with the business currency, e.g. 12050 -> "₱120.50". */
export function formatMinor(minor: number, currency = 'PHP', locale = 'en-PH'): string {
  const fd = fractionDigitsFor(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: fd,
      maximumFractionDigits: fd,
    }).format(minor / 10 ** fd);
  } catch {
    return `${currency} ${minorToInput(minor, fd)}`;
  }
}

export function fractionDigitsFor(currency: string): number {
  return ['JPY', 'KRW', 'VND'].includes(currency.toUpperCase()) ? 0 : 2;
}

/** SKUs are stored upper-case; letters, digits and . _ / - only, up to 64 characters. */
export const SKU_PATTERN = /^[A-Z0-9][A-Z0-9._/-]{0,63}$/;
export function normalizeSku(input: string | null | undefined): string | null {
  const v = (input ?? '').trim().toUpperCase().replace(/\s+/g, '-');
  return v === '' ? null : v;
}
export function isValidSku(sku: string): boolean {
  return SKU_PATTERN.test(sku);
}

/** Barcodes keep their case (some symbologies are alphanumeric); no spaces, up to 64 characters. */
export const BARCODE_PATTERN = /^[A-Za-z0-9-]{1,64}$/;
export function normalizeBarcode(input: string | null | undefined): string | null {
  const v = (input ?? '').replace(/\s+/g, '');
  return v === '' ? null : v;
}
export function isValidBarcode(barcode: string): boolean {
  return BARCODE_PATTERN.test(barcode);
}

/** Selection rule of a modifier group. `max_select` null means "no upper limit". */
export interface ModifierRule {
  min_select: number;
  max_select: number | null;
}

/** Human-readable problem with a rule, or null when it is valid. */
export function validateModifierRule(rule: ModifierRule): string | null {
  if (!Number.isInteger(rule.min_select) || rule.min_select < 0)
    return 'Minimum must be a whole number of 0 or more';
  if (rule.max_select !== null) {
    if (!Number.isInteger(rule.max_select) || rule.max_select < 1)
      return 'Maximum must be a whole number of 1 or more, or empty for no limit';
    if (rule.max_select < rule.min_select) return 'Maximum cannot be below the minimum';
  }
  return null;
}

export function isRequiredGroup(rule: ModifierRule): boolean {
  return rule.min_select >= 1;
}

/** Whether `count` chosen options satisfy the rule (used by the cart in a later phase). */
export function selectionSatisfies(rule: ModifierRule, count: number): boolean {
  if (count < rule.min_select) return false;
  if (rule.max_select !== null && count > rule.max_select) return false;
  return true;
}

/** Short label for a rule as shown in lists: "optional", "required", "pick 1", "pick 1 to 3". */
export function describeModifierRule(rule: ModifierRule): string {
  const { min_select: min, max_select: max } = rule;
  if (min === 0 && max === null) return 'optional';
  if (min === 0) return `up to ${max}`;
  if (max === null) return `at least ${min}`;
  if (min === max) return `pick ${min}`;
  return `pick ${min} to ${max}`;
}

/** Swap an item with its neighbour; null when the move is impossible. */
export function moveInOrder<T>(ids: readonly T[], index: number, dir: -1 | 1): T[] | null {
  const j = index + dir;
  if (index < 0 || index >= ids.length || j < 0 || j >= ids.length) return null;
  const out = [...ids];
  const a = out[index] as T;
  const b = out[j] as T;
  out[index] = b;
  out[j] = a;
  return out;
}

/** Trim and collapse a person-facing name; null when empty. */
export function cleanName(input: string): string {
  return input.replace(/\s+/g, ' ').trim();
}
